import { Link } from "@tanstack/react-router";
import { Phone, MapPin, Clock, Mail, Instagram, Facebook } from "lucide-react";
import blancoLogo from "@/assets/blanco-logo-sm.webp.asset.json";
import footerBg from "@/assets/metallic-hero.jpg";

export function Footer() {
  return (
    <footer className="relative bg-foreground text-background overflow-hidden">
      {/* Epoxy background image */}
      <div className="pointer-events-none absolute inset-0">
        <img
          src={footerBg}
          alt="Metallic epoxy floor finish by Blanco Pro Services"

          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground/85 via-foreground/90 to-foreground" />
      </div>
      {/* Decorative gold glows */}
      <div className="pointer-events-none absolute -top-32 -left-32 w-[32rem] h-[32rem] rounded-full bg-gradient-gold blur-3xl opacity-10" />
      <div className="pointer-events-none absolute -bottom-40 right-0 w-[28rem] h-[28rem] rounded-full bg-gradient-gold blur-3xl opacity-[0.07]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* MARQUEE BANNER */}
        <div className="border-y border-background/10 -mx-4 sm:-mx-6 lg:-mx-8 mb-20 overflow-hidden">
          <div className="flex gap-12 py-8 whitespace-nowrap animate-[marquee_40s_linear_infinite]">
            {Array.from({ length: 2 }).map((_, dup) => (
              <div key={dup} className="flex items-center gap-12 shrink-0">
                {[
                  "Metallic Epoxy",
                  "Flake Floors",
                  "Polished Concrete",
                  "Polyaspartic Coatings",
                  "Hardscaping",
                  "Demolition",
                  "Warehouse Coatings",
                  "Garage Storage",
                ].map((w) => (
                  <span key={`${dup}-${w}`} className="flex items-center gap-12 font-display text-4xl md:text-5xl font-bold text-background/15">
                    {w}
                    <span className="w-2 h-2 rounded-full bg-gold shrink-0" />
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* TOP CTA ROW */}
        <div className="mb-20">
          <h2 className="font-display text-4xl md:text-6xl font-bold leading-[1.02]">
            <em className="not-italic bg-gradient-gold bg-clip-text text-transparent">Built to Last.</em>
          </h2>
        </div>


        {/* MAIN GRID */}
        <div className="grid md:grid-cols-12 gap-10 pb-16 border-b border-background/10">
          {/* Brand column */}
          <div className="md:col-span-4">
            <div className="flex items-center gap-3 mb-5">
              <img src={blancoLogo.url} alt="Blanco Pro Services" width={96} height={79} loading="lazy" decoding="async" className="w-12 h-12 object-contain" />
              <div className="font-display text-lg font-bold leading-tight">
                Blanco Pro<br />
                <span className="text-xs uppercase tracking-[0.25em] text-background/50 font-normal">Services</span>
              </div>
            </div>
            <p className="text-sm text-background/60 leading-relaxed mb-6 max-w-xs">
              Premium epoxy flooring, hardscaping & demolition serving New Jersey, Pennsylvania, and New York since 2016.
            </p>
            <div className="flex gap-3">
              {[
                { Icon: Instagram, href: "https://www.instagram.com/blancoproservices", label: "Blanco Pro Services on Instagram" },
                { Icon: Facebook, href: "https://www.facebook.com/share/1S9PwsjGyB/", label: "Blanco Pro Services on Facebook" },
              ].map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-10 h-10 rounded-full border border-background/15 flex items-center justify-center hover:border-gold hover:text-gold hover:bg-background/[0.03] transition-colors"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>

          </div>

          {/* Navigate */}
          <div className="md:col-span-2">
            <h3 className="font-display text-xs uppercase tracking-[0.25em] text-gold mb-5">Navigate</h3>
            <ul className="space-y-3 text-sm text-background/70">
              {[
                { to: "/", label: "Home" },
                { to: "/services", label: "Services" },
                { to: "/service-area", label: "Service Area" },
                { to: "/about", label: "About" },
                { to: "/contact", label: "Contact" },
                { to: "/blog", label: "Guides" },
              ].map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="inline-flex items-center gap-2 group hover:text-gold transition-colors">
                    <span className="w-0 h-px bg-gold transition-all group-hover:w-3" />
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="md:col-span-3">
            <h3 className="font-display text-xs uppercase tracking-[0.25em] text-gold mb-5">Services</h3>
            <ul className="space-y-3 text-sm text-background/70">
              {[
                "Metallic Epoxy",
                "Flake Floors",
                "Polished Concrete",
                "Polyaspartic Coatings",
                "Hardscaping & Pavers",
                "Warehouse Coatings",
                "Demolition",
              ].map((s) => (
                <li key={s} className="inline-flex items-center gap-2 hover:text-gold transition-colors cursor-default">
                  <span className="w-1 h-1 rounded-full bg-gold/40" />
                  {s}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact card */}
          <div className="md:col-span-3">
            <h3 className="font-display text-xs uppercase tracking-[0.25em] text-gold mb-5">Visit / Contact</h3>
            <div className="rounded-2xl border border-background/10 bg-background/[0.02] p-5 backdrop-blur space-y-4 text-sm">
              <a href="tel:9088956133" className="flex items-center gap-3 group hover:text-gold transition-colors">
                <span className="w-9 h-9 rounded-lg bg-gradient-gold flex items-center justify-center shrink-0 shadow-gold">
                  <Phone className="w-4 h-4 text-gold-foreground" />
                </span>
                <span>
                  <div className="text-[10px] uppercase tracking-widest text-background/50">Call</div>
                  908-895-6133
                </span>
              </a>
              <a href="mailto:service@blancopros.com" className="flex items-center gap-3 group hover:text-gold transition-colors">
                <span className="w-9 h-9 rounded-lg border border-background/15 flex items-center justify-center shrink-0">
                  <Mail className="w-4 h-4" />
                </span>
                <span className="break-all">
                  <div className="text-[10px] uppercase tracking-widest text-background/50">Email</div>
                  service@blancopros.com
                </span>
              </a>
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-lg border border-background/15 flex items-center justify-center shrink-0">
                  <MapPin className="w-4 h-4" />
                </span>
                <span>
                  <div className="text-[10px] uppercase tracking-widest text-background/50">HQ</div>
                  Serving NJ, PA & NY
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-lg border border-background/15 flex items-center justify-center shrink-0">
                  <Clock className="w-4 h-4" />
                </span>
                <span>
                  <div className="text-[10px] uppercase tracking-widest text-background/50">Hours</div>
                  Mon–Sat · 7:30a – 4:30p
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* BOTTOM BAR */}
        <div className="pt-8 pb-8 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-background/50">
          <div className="flex flex-wrap items-center gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
            © {new Date().getFullYear()} Blanco Pro Services · All rights reserved
            <span className="w-1 h-1 rounded-full bg-background/20" />
            <a
              href="https://elroi.marketing/"
              target="_blank"
              rel="noopener"
              className="uppercase tracking-widest hover:text-gold transition-colors"
            >
              Web Development by El Roi Marketing
            </a>
          </div>

          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-gold transition-colors">Privacy Policy</Link>
            <span className="w-1 h-1 rounded-full bg-background/20" />
            <Link to="/terms" className="hover:text-gold transition-colors">Terms & Conditions</Link>
            <span className="w-1 h-1 rounded-full bg-background/20" />
            <Link to="/accessibility" className="hover:text-gold transition-colors">Accessibility</Link>
            <span className="w-1 h-1 rounded-full bg-background/20" />
            <span>Serving NJ, PA & NY</span>
          </div>


        </div>
      </div>

      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </footer>
  );
}
